import React, { useEffect, useState } from 'react';
import { Button, Flex, Modal, Tooltip, notification } from 'antd';
import { EditOutlined, EyeOutlined, FileDoneOutlined } from '@ant-design/icons';
import './styles.css';
import TableComponent from '../other/TableComponent';
import NdaPopup from './NdaPopup';
import { contracts } from '../api';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { handelDate, handlestatus } from '../other/usefulFunctions';
import Filter from '../other/Filter';
import { userRole, employeeId } from '../../GetRole.utils';

const Nda = () => {
  const location = useLocation();
  const { state } = location;
  const [loadings, setLoadings] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [contractsData, setContractsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecordCount, setTotalRecordCount] = useState();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showBtn, setShowBtn] = useState(false);
  const [filterFields, setFilterFields] = useState({
    page: 1,
    filterField: '',
    sorting: { field: '', order: '' },
    filterValue: '',
  });
  const navigate = useNavigate();
  const [clearForm, setClearForm] = useState(false);
  const [apiHit, setApiHit] = useState(false);

  const getInitialStatus = () => {
    const statusParam = searchParams.get('status');
    return statusParam ? statusParam.split(',').map(Number) : [1, 2, 3];
  };

  const getInitialExpiredDays = () => {
    return searchParams.get('expiredDays') || '';
  };

  const [contractFilters, setContractFilters] = useState({
    status: getInitialStatus(),
    expiredDays: getInitialExpiredDays(),
  });

  const openNotificationWithIcon = (type, message) => {
    notification[type]({
      message: message,
      style: {
        padding: '9px 24px',
      },
    });
  };

  const handleFilters = (value) => {
    setFilterFields((prevState) => {
      if (
        prevState.page !== value.page ||
        prevState.filterField !== value.filterField ||
        prevState.sorting.field !== value.sorting.field ||
        prevState.sorting.order !== value.sorting.order ||
        prevState.filterValue !== value.filterValue
      ) {
        return {
          page: value.page,
          filterField: value.filterField,
          sorting: {
            field: value.sorting.field,
            order: value.sorting.order,
          },
          filterValue: value.filterValue,
        };
      } else {
        return prevState;
      }
    });
  };

  const handleContractStatus = (values) => {
    setContractFilters((prev) => {
      const newFilters = { ...prev, status: values };
      setSearchParams((params) => {
        params.set('status', values.join(','));
        params.set('expiredDays', newFilters.expiredDays);
        return params;
      });
      return newFilters;
    });
  };

  const handleExpireDays = (value) => {
    setContractFilters((prev) => {
      const newFilters = { ...prev, expiredDays: value };
      setSearchParams((params) => {
        params.set('status', newFilters.status.join(','));
        params.set('expiredDays', value);
        return params;
      });
      return newFilters;
    });
  };

  const showModal = (key) => {
    setSelectedKey(key);
    setIsModalOpen(true);
    setClearForm(!clearForm);
    setApiHit(!apiHit);
  };

  const handlePopupCancel = () => {
    setSelectedKey(null);
    setIsModalOpen(false);
  };

  const getTitle = () => {
    return selectedKey
      ? 'Edit Non-Disclosure Agreement (NDA)'
      : 'Add Non-Disclosure Agreement (NDA)';
  };

  const handleApproval = (id) => {
    const postData = {
      cStatusId: 2,
      createdBy: employeeId,
    };
    setLoadings(id);
    contracts
      .updateContractsData(id, postData)
      .then((res) => {
        openNotificationWithIcon('success', 'Contract sent for Approval');
        setShowBtn((prev) => !prev);
        setLoadings(null);
      })
      .catch((err) => {
        console.log(err);
        openNotificationWithIcon('error', 'Error in Sending Approval.');
        setLoadings(null);
      });
  };

  const columns = [
    {
      title: 'Client',
      dataIndex: 'legalEntityName',
      key: 'legalEntityName',
      width: '8%',
    },
    {
      title: 'File',
      dataIndex: 'fileName',
      key: 'fileName',
      change: true,
      width: '12%',
      render: (_, record) => (
        <a target="_blank" rel="noreferrer" href={record.fileUrl}>
          {record.fileName}
        </a>
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'activationDate',
      key: 'activationDate',
      width: '6%',
      render: (_, record) => handelDate(record.activationDate),
    },
    {
      title: 'End Date',
      dataIndex: 'expiryDate',
      width: '6%',
      key: 'expiryDate',
      render: (_, record) => handelDate(record.expiryDate),
    },
    {
      title: 'Last Updated On',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: '8%',
      render: (_, record) => handelDate(record.updatedAt),
    },
    {
      title: 'Status',
      dataIndex: 'cStatus',
      key: 'cStatus',
      width: '6%',
      render: (_, { cStatus }) => {
        return handlestatus(cStatus);
      },
    },
    {
      title: 'Actions',
      dataIndex: 'action',
      key: 'action',
      width: '8%',
      fixed: 'right',
      render: (_, record) => (
        <div
          style={{ display: 'flex', gap: '8px', justifyContent: 'flex-start' }}
        >
          <div style={{ width: '32px', textAlign: 'center' }}>
            {(record.cStatus === 'draft' ||
              record.cStatus === 'pendingapproval' ||
              record.cStatus === 'inactive') &&
            userRole !== 'finance' ? (
              <Tooltip title="Edit" key="Edit">
                <Button
                  onClick={() => showModal(record.contractId)}
                  type="link"
                  style={{ color: '#003eb3' }}
                  icon={<EditOutlined />}
                />
              </Tooltip>
            ) : null}
          </div>

          <div style={{ width: '32px', textAlign: 'center' }}>
            {
              <Tooltip title="View" key="View">
                <Button
                  type="link"
                  style={{ color: '#fa541c' }}
                  icon={<EyeOutlined />}
                  onClick={() => navigate(`/contract/nda/${record.contractId}`)}
                />
              </Tooltip>
            }
          </div>

          <div style={{ width: '32px', textAlign: 'center' }}>
            {record.cStatus === 'draft' && userRole !== 'finance' ? (
              <Tooltip title="Send for Approval" key="Approval">
                <Button
                  loading={loadings === record.contractId}
                  type="link"
                  style={{ color: '#52c41a' }}
                  icon={<FileDoneOutlined />}
                  onClick={() => handleApproval(record.contractId)}
                />
              </Tooltip>
            ) : null}
          </div>

          <Modal
            title={getTitle()}
            key={isModalOpen ? 'open' : 'closed'}
            visible={isModalOpen && selectedKey === record?.contractId}
            onCancel={handlePopupCancel}
            footer={null}
            destroyOnClose={true}
          >
            <NdaPopup
              hide={setIsModalOpen}
              isModalOpen={isModalOpen}
              buttonText={'Update'}
              clientId={record?.contractId}
              status={record.cStatus}
              apiHit={apiHit}
            />
          </Modal>
        </div>
      ),
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await contracts.contractsAllData(
        filterFields.page,
        'nda',
        contractFilters.status,
        filterFields.sorting.field,
        filterFields.sorting.order,
        filterFields.filterField,
        filterFields.filterValue,
        contractFilters.expiredDays
      );
      setContractsData(response.data.data);
      setTotalRecordCount(response.data.totalContractCount);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [isModalOpen, contractFilters, filterFields, showBtn]);

  return (
    <>
      <Flex justify="space-between">
        <h2>Non-Disclosure Agreement (NDA)</h2>
        {userRole !== 'finance' && (
          <Button onClick={() => showModal(null)} type="primary">
            Add NDA
          </Button>
        )}

        <Modal
          key={isModalOpen ? 'open' : 'closed'}
          title={getTitle()}
          open={isModalOpen && !selectedKey}
          onCancel={() => {
            handlePopupCancel();
          }}
          footer={null}
          destroyOnClose={true}
        >
          <NdaPopup
            isModalOpen={isModalOpen}
            hide={setIsModalOpen}
            buttonText={'Save as Draft'}
            clearForm={clearForm}
          />
        </Modal>
      </Flex>
      <Flex style={{ position: 'relative' }} justify="end">
        <Filter
          handleContractStatus={handleContractStatus}
          handleExpireDays={handleExpireDays}
          expiredDays={contractFilters.expiredDays}
          status={contractFilters.status}
        />
      </Flex>
      <TableComponent
        loading={loading}
        data={contractsData}
        columns={columns}
        scrollX={1200}
        totalPages={totalRecordCount}
        onFilters={handleFilters}
      />
    </>
  );
};

export default Nda;
